import styles from '../map.module.css';
import {
    useMap,
    ControlPosition,
    MapControl
} from '@vis.gl/react-google-maps';
import DmsCoordinates from "dms-conversion";
import parse from 'html-react-parser';
import { isNumeric } from '../../../helpers/ObjectHelpers';
import ShipImage from '../../ShipImage';
import MapPropertiesService from '../services/MapPropertiesService';
import React, { useEffect, useState } from 'react';
import MapApiRepository from '../repositories/MapApiRepository';
import { useNavigate, useSearchParams } from 'react-router';
import moment from "moment";
import { getElementById } from '../../../helpers/ElementHelpers';
import { Button } from '@progress/kendo-react-buttons';
import { MapPin, X } from 'phosphor-react-sc';
import { isMobile } from '../../../helpers/DeviceHelpers';
import { getBooleanSearchParam } from '../../../helpers/UrlHelpers';
import {
    useMapId,
    useShowInfo,
    useSetShowInfo,
    useShowLegend,
    useSelectedCoastalSegmentGroup,
    useSetSelectShipPosition,
    useSetShowDialog,
    useSetSelectedLocation,
    useHasQuayManualAccess,
    useShowLocationQuayManual
} from '../hooks/useMapStore';
import { MapInfoPanelCoastalSegmentGroup  } from './info-panels/MapInfoPanelCoastalSegmentGroup';

export const MapMarkerInfoPanel = () => {

    const [searchParams, _] = useSearchParams();

    const mapId = useMapId();
    const stateShowInfo = useShowInfo();
    const stateShowLegend = useShowLegend();
    const selectedCoastalSegmentGroup = useSelectedCoastalSegmentGroup();

    const map = useMap();

    const [showLegend, setShowLegend] = useState(getBooleanSearchParam(searchParams, "sleg"));
    const [marker, setMarker] = useState<any>(null);

    const id = `googleMapInfoWindow-${mapId}`;
    const withLegendClassName = "google-map-info-window-parent-with-legend";
    const withoutLegendClassName = "google-map-info-window-parent-without-legend";

    const handleShowInfoMessage = (data: any) => {
        const elem = getElementById(mapId); // Your map container

        setMarker(data);

        if (!elem || !map || !data || !isMobile()) return;

        const mapHeight = elem.offsetHeight;

        const projection = map.getProjection();
        if (!projection) return;

        const bounds = map.getBounds();
        if (!bounds) return;

        const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
        if (!topRight) return;

        const markerWorldPoint = projection.fromLatLngToPoint(data.position);
        if (!markerWorldPoint) return;

        const zoom = map.getZoom();
        if (!zoom) return;

        const scale = Math.pow(2, zoom);
        const markerPixelY = (markerWorldPoint.y - topRight.y) * scale;

        const distanceFromBottom = mapHeight - markerPixelY;
        if (distanceFromBottom < 170) {
            const north = bounds.getNorthEast().lat();
            const south = bounds.getSouthWest().lat();
            const mapDegrees = north - south;

            const pixelOffset = 210 - distanceFromBottom;
            const latOffset = (pixelOffset / mapHeight) * mapDegrees;

            map.setCenter(
                {
                    lat: data.position.lat() + latOffset,
                    lng: data.position.lng()
                }
            )
        }

    }

    const addClassName = (elem: HTMLElement, className: string) => {
        if (!elem.classList.contains(className)) {
            elem.classList.add(className);
        }
    }

    const removeClassName = (elem: HTMLElement, className: string) => {
        if (elem.classList.contains(className)) {
            elem.classList.remove(className);
        }
    }

    useEffect(() => {
        if (!marker && !selectedCoastalSegmentGroup) return;
        setTimeout(() => {
            const elem = getElementById(id);
            if (!elem) return;

            const parent = elem.parentElement;
            if (!parent) return;

            addClassName(parent, "google-map-info-window-parent");
            if (showLegend) {
                removeClassName(parent, withoutLegendClassName);
                addClassName(parent, withLegendClassName);

            } else {
                removeClassName(parent, withLegendClassName);
                addClassName(parent, withoutLegendClassName);
            }
        }, 75);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marker, showLegend, selectedCoastalSegmentGroup])

    useEffect(() => {
        handleShowInfoMessage(stateShowInfo);
    }, [stateShowInfo]);

    useEffect(() => {
        setShowLegend(stateShowLegend);
    }, [stateShowLegend]);

    return (
        (isMobile()) &&
        <MapControl position={ControlPosition.BOTTOM_CENTER}>
            <div id={id} className={styles.mainInfoPanel}>
                <MapInfoPanelCoastalSegmentGroup />
                {
                    (marker) &&
                    <MapMarkerInfoPanelContent marker={marker} />
                }
            </div>
        </MapControl>
    )
}

interface MapMarkerInfoPanelContentProps {
    marker: any,
}
const MapMarkerInfoPanelContent = ({
    marker,
}: MapMarkerInfoPanelContentProps) => {

    const setSelectShipPosition= useSetSelectShipPosition();
    const setShowDialog = useSetShowDialog();
    const setShowInfo = useSetShowInfo();
    const setSelectedLocation = useSetSelectedLocation();

    const mapMarkerTypes = MapPropertiesService.markerTypes;

    return (
        (marker) &&
        <div className={`google-map-info-window google-map-info-window-mobile`}>
                <div className="google-map-info-window-header">
                    <div className="font-size-lg text-truncate">
                        {getInfoWindowHeaderContent()}
                    </div>
                    <div onClick={() => {
                        setShowInfo(null);
                        setSelectShipPosition(null);
                        setSelectedLocation(null);
                    }}>
                        <X height={20} width={20} />
                    </div>
                </div>
                 <div className="google-map-info-window-body">
                    {(() => {
                        switch (marker.type) {
                            case mapMarkerTypes.Ship:
                                return (<MapMarkerInfoPanelShipContent
                                    marker={marker}
                                    onClick={() => {
                                        setShowDialog(marker);
                                    }}
                                />)
                            case mapMarkerTypes.Location:
                                return <MapMarkerInfoPanelLocationContent marker={marker} />
                            case mapMarkerTypes.CertificateLocation:
                                return <MapMarkerInfoPanelCertificateLocationContent marker={marker} />
                            default:
                                return <MapMarkerInfoPanelLatLngContent marker={marker} />
                        }
                        })()}
                </div>
            </div>
    )

    function getInfoWindowHeaderContent() {
        switch (marker.type) {
            case mapMarkerTypes.Ship:
                return `${marker.description} (${marker.callSign})`;
            default:
                return marker.description;
        }
    }
}

interface MapMarkerInfoPanelShipContentProps {
    marker: any,
    onClick: () => void,
}
const MapMarkerInfoPanelShipContent = ({
    marker,
    onClick
}: MapMarkerInfoPanelShipContentProps) => {

    const speedOverGround = isNumeric(marker.speedOverGround) ? marker.speedOverGround.toFixed(1) : 0;
    const courseOverGround = isNumeric(marker.courseOverGround) ? marker.courseOverGround.toFixed(1) : 0;
    const shipId = isNumeric(marker.shipId) ? marker.shipId : 0;

    return (
        <>
            <ShipImage imoNo={marker.imoNo} description={marker.description} />
            <div className="google-map-info-window-content">
                <div className="k-d-grid k-gap-4 grid-cols-auto-fr">
                    <MapMarkerInfoPanelLatLngContent marker={marker} />
                </div>
            </div>
            <div className="google-map-info-window-toolbar">
                <div className="font-size-md k-p-2" style={{ borderRight: "solid 1px var(--card-border)"} }>
                    COG / SOG<br />
                    <strong>{courseOverGround}&#176; / {speedOverGround}kn</strong>
                </div>
                <div className="font-size-md text-align-center" style={{ borderRight: "solid 1px var(--card-border)", alignContent: "space-around" }}>
                    {
                        (shipId === 0) &&
                        <strong>{parse('Fart&oslash;yinformasjon er ikke registrert i grunnlagsdata')}</strong>
                    }
                    
                </div>
                <div className="font-size-md">
                    {
                        (shipId > 0) &&
                            <Button size="small" themeColor="info" onClick={onClick}>Vis detaljer</Button>
                    }
                </div>
            </div>
        </>
    )
}

interface MapMarkerInfoPanelCertificateLocationContentProps {
    marker: {
        description: string,
        certificateStatus: string,
        lastPilotageDate: string
    }
}

const MapMarkerInfoPanelCertificateLocationContent = ({
    marker
}: MapMarkerInfoPanelCertificateLocationContentProps) => {

    let lastPilotageDate = " ";
    const obj = moment(marker.lastPilotageDate);
    if (obj.isValid() && obj.year() > 1970)
    {
        lastPilotageDate = obj.format("DD.MM.YYYY");
    }

    return (
        <>
            <div className="google-map-info-window-text font-size-md">
                <MapPin height={90} width={90} weight="fill" />
            </div>
            <div className="google-map-info-window-content">
                <div className="k-d-grid k-gap-4 grid-cols-auto-fr">
                    <MapMarkerInfoPanelLatLngContent marker={marker} />
                </div>
            </div>
            <div className="google-map-info-window-toolbar">
                <div className="font-size-md text-align-center k-p-2"
                    style={{
                        borderRight: "solid 1px #d2d3d7",
                        alignContent: "space-around",
                    }}>
                    Sist seilt:<br/>
                    <strong>{lastPilotageDate}</strong>
                </div>
                <div className="font-size-md text-align-center k-p-2"
                    style={{
                        borderRight: "solid 1px #d2d3d7",
                        alignContent: "space-around",
                        gridColumn: "2 / span 2"
                    }}>
                    Sertifikatstatus:<br />
                    <strong>{marker.certificateStatus}</strong>
                </div>
            </div>

        </>
    )
}

interface MapMarkerInfoPanelLocationContentProps {
    marker: any
}
const MapMarkerInfoPanelLocationContent = ({
    marker
}: MapMarkerInfoPanelLocationContentProps) => {

    const navigate = useNavigate();

    const [hasQuayManuals, setHasQuayManuals] = useState(false);
    const { hasQuayManualAccess } = useHasQuayManualAccess();

    const showLocationQuayManual = useShowLocationQuayManual();

    useEffect(() => {
        if (!hasQuayManualAccess || !showLocationQuayManual) return;
        const initialize = async () => {
            const response = await MapApiRepository.getQuayManualByLocationAndProximity(marker.id);
            if (response.ok) {
                const result = await response.json();
                setHasQuayManuals(result !== null && result.length > 0);
                marker.hasQuayManuals = result !== null && result.length > 0;
            }
        }
        initialize();
    }, [marker, hasQuayManualAccess, showLocationQuayManual]);

    return (
        <>
            <div className="google-map-info-window-text font-size-md">
                <MapPin height={90} width={90} color={marker.color} weight="fill" />
            </div>
            <div className="google-map-info-window-content" style={{ padding: 10 }}>
                <div className="k-d-grid k-gap-4 grid-cols-auto-fr">
                    <MapMarkerInfoPanelLatLngContent marker={marker} />
                </div>
            </div>
                <div className="google-map-info-window-toolbar">
                <div className="font-size-md" />
                <div className="font-size-md text-align-center" style={{ borderRight: "solid 1px #d2d3d7", alignContent: "space-around" }}>
                </div>
                    <div className="font-size-md">
                        {
                            (hasQuayManuals) &&
                            <Button size="small" themeColor="info" onClick={onClick}>
                                Kaibok
                            </Button>
                        }
                    </div>
                </div>
            
        </>
    )
    function onClick() {
        navigate(`/quaymanuals/${marker.id}/proximity?title=${encodeURI(marker.description)}`)
    }
}

interface MapMarkerInfoPanelLatLngContentProps {
    marker: any
}
const MapMarkerInfoPanelLatLngContent = ({
    marker
}: MapMarkerInfoPanelLatLngContentProps) => {

    const dmsCoords = new DmsCoordinates(marker.position.lat(), marker.position.lng());
    const { longitude, latitude } = dmsCoords.dmsArrays;
    
    return (
        <div className="k-d-grid k-gap-1 font-size-md" style={{ padding: 10 }}>
            Posisjon: <br />
            <strong>
                {parseCoordinates(latitude)}<br/>
                {parseCoordinates(longitude)}
            </strong>
        </div>
    )

    function parseCoordinates(coordinate: any) {
        const [d, m, s, nsew] = coordinate;
        return (
            parse(`${d}&#176;${m}'${s.toFixed(0)}" ${getCoordinateDirection(nsew)}`)
        )
    }

    function getCoordinateDirection(nsew: string) {
        switch (nsew) {
            case "E":
                return "&Oslash;";
            case "W":
                return "V";
            default:
                return nsew;
        }
    }
}
